import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold transition-all duration-400 liquid-morph focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "glass-liquid-medium bg-primary/80 text-primary-foreground hover:bg-primary/90 hover-liquid shadow-liquid-md hover:shadow-liquid-lg backdrop-blur-xl",
        destructive: "glass-liquid-medium bg-destructive/80 text-destructive-foreground hover:bg-destructive/90 hover-liquid shadow-liquid-md",
        outline: "glass-liquid-light border-2 border-border-glass-medium hover:bg-accent/50 hover-liquid",
        secondary: "glass-liquid-medium bg-secondary/80 text-secondary-foreground hover:bg-secondary/90 hover-liquid shadow-liquid-md",
        ghost: "hover:glass-liquid-light hover-liquid",
        link: "text-primary underline-offset-4 hover:underline liquid-morph",
      },
      size: {
        default: "h-11 px-6 py-2.5 rounded-full",
        sm: "h-9 rounded-full px-4",
        lg: "h-12 rounded-full px-8",
        icon: "h-11 w-11 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
